import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { AlertCircle, ExternalLink, Play } from 'lucide-react';

interface YouTubePlayerProps {
  /** Orice formă de URL/ID YouTube: watch?v=, youtu.be/, shorts/, embed/, sau id-ul brut */
  url: string;
  title?: string;
  /** Dacă true, iframe-ul se încarcă direct (util în popup deschis la user gesture) */
  autoplay?: boolean;
  className?: string;
}

/**
 * URL-ul unui host HTTPS real pe care este servit `public/yt.html`.
 * Acesta este origin-ul pe care YouTube îl vede când se încarcă IFrame API-ul — de aceea
 * NU poate fi `capacitor://localhost` (YouTube returnează Error 153 pe scheme custom).
 *
 * IMPORTANT: pe Capacitor, iframe-ul este configurat să încarce `${WEB_BASE}/yt.html?id=…`
 * astfel încât YouTube vede un Referer `https://fiifit.ro` valid.
 */
const WEB_BASE =
  (import.meta.env.VITE_WEB_BASE_URL as string | undefined) || 'https://fiifit.ro';

/**
 * Player YouTube unificat pentru Web + Capacitor (iOS/Android).
 *
 * Strategie anti Error 153:
 *  - PE WEB: iframe standard către youtube.com/embed (funcționează perfect, origin real).
 *  - PE CAPACITOR: iframe către pagina proxy HTTPS `https://fiifit.ro/yt.html?id=XXX`.
 *    Această pagină este o copie minimalistă care conține YouTube IFrame API pe un origin
 *    HTTPS acceptat de YouTube → NU mai apare Error 153.
 *
 *  - Facade pattern: afișăm thumbnail + buton play. La click → injectăm iframe-ul. Asta
 *    garantează autoplay-ul (user gesture) pe toate platformele.
 *  - Buton mic discret pentru fallback manual (deschide SafariViewController pe mobile,
 *    tab nou pe web).
 */
export default function YouTubePlayer({
  url,
  title = 'YouTube video',
  autoplay = false,
  className = '',
}: YouTubePlayerProps) {
  const videoId = extractYouTubeId(url);
  const [isPlaying, setIsPlaying] = useState(autoplay);

  const isNative = Capacitor.isNativePlatform();

  const buildEmbedUrl = () => {
    if (isNative) {
      // Pe Capacitor → încărcăm proxy-ul servit de domeniul nostru HTTPS
      return `${WEB_BASE}/yt.html?id=${videoId}&autoplay=1`;
    }
    // Pe web → embed direct (origin real, funcționează perfect)
    const origin = window.location.origin;
    const qs = new URLSearchParams({
      autoplay: '1',
      playsinline: '1',
      rel: '0',
      modestbranding: '1',
      fs: '1',
      origin,
    }).toString();
    return `https://www.youtube.com/embed/${videoId}?${qs}`;
  };

  const openInYouTube = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const target = videoId ? `https://www.youtube.com/watch?v=${videoId}` : url;
    if (isNative) {
      await Browser.open({
        url: target,
        presentationStyle: 'popover',
        windowName: '_self',
      });
    } else {
      window.open(target, '_blank', 'noopener,noreferrer');
    }
  };

  if (!videoId) {
    return (
      <div
        className={`aspect-video rounded-lg bg-gray-900 flex items-center justify-center text-white text-sm ${className}`}
      >
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>URL video invalid</span>
        </div>
      </div>
    );
  }

  // Player activ: iframe real
  if (isPlaying) {
    return (
      <div
        className={`relative aspect-video rounded-lg overflow-hidden bg-black ${className}`}
      >
        <iframe
          src={buildEmbedUrl()}
          title={title}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          loading="eager"
        />
        <button
          onClick={openInYouTube}
          title="Deschide în YouTube"
          className="absolute top-2 right-2 z-10 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition"
        >
          <ExternalLink className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Facade: thumbnail + buton play
  return (
    <button
      type="button"
      onClick={() => setIsPlaying(true)}
      className={`relative aspect-video rounded-lg overflow-hidden bg-black group w-full ${className}`}
      aria-label={`Play: ${title}`}
    >
      <img
        src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src = `https://img.youtube.com/vi/${videoId}/0.jpg`;
        }}
      />
      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-20 h-14 bg-red-600 rounded-xl flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
          <Play className="w-8 h-8 text-white ml-1" fill="white" />
        </div>
      </div>
    </button>
  );
}

/**
 * Extrage ID-ul YouTube din orice format uzual.
 */
export function extractYouTubeId(input: string): string {
  if (!input) return '';
  const raw = input.trim();

  if (/^[a-zA-Z0-9_-]{11}$/.test(raw)) return raw;

  const patterns = [
    /[?&]v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube-nocookie\.com\/embed\/([a-zA-Z0-9_-]{11})/,
  ];

  for (const re of patterns) {
    const m = raw.match(re);
    if (m && m[1]) return m[1];
  }
  return '';
}
